import { useCallback } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { useTranslation } from 'react-i18next'

import { DownloadModal } from '~/pages/AdminUploads/components/DownloadModal'
import { useExportCisContracts } from '~/pages/AdminUploads/hooks/useExportCisContracts'
import type { CisContractCsvExportRow } from '~/pages/AdminUploads/models/cisContractCsvExport'
import {
  buildCisContractCsvExport,
  cisContractCsvExportFileName,
  toCisContractCsvExportRows,
} from '~/pages/AdminUploads/models/cisContractCsvExport'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 入稿済み供給地点マスタのダウンロード確認モーダル
 * (uploads/common.md §6 / AC-DUC-028 / AC-DUC-032)。
 *
 * モーダル文言のデータ種別名は spec 準拠の「供給地点マスタ」(画面の訳語)。ファイル名・
 * CSV のヘッダー・区分値は表示言語に応じて日本語 / 英語で出す (common.md §6)。
 * [2026-09-29 / #5793 追従完了] 以前はデータ種別一覧の行ラベルだけ「CIS契約マスタ」の
 * ままで、同一画面内に過渡的な不一致があった。行ラベルを正本へ揃えたので解消済み。
 */
export const CisContractDownloadModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 英語表示ではファイル名・ヘッダー行・区分値を英語で出す (common.md §6)。
  const isEnglish = i18n.language.startsWith('en')
  const { mutateAsync: exportAsync } = useExportCisContracts()
  const typeName = t('page:adminUploads.export.supplyPointMasterName')

  const exportRows = useCallback(async () => {
    const resp = await exportAsync({})
    return {
      rows: toCisContractCsvExportRows(resp.rows),
      dataUpdatedAt:
        resp.dataUpdatedAt !== undefined
          ? timestampDate(resp.dataUpdatedAt)
          : undefined,
    }
  }, [exportAsync])

  const buildCsv = useCallback(
    (rows: CisContractCsvExportRow[]) =>
      buildCisContractCsvExport(rows, isEnglish),
    [isEnglish],
  )

  const fileName = useCallback(
    () => cisContractCsvExportFileName(isEnglish),
    [isEnglish],
  )

  return (
    <DownloadModal<CisContractCsvExportRow>
      buildCsv={buildCsv}
      exportRows={exportRows}
      fileName={fileName}
      isOpen={isOpen}
      onClose={onClose}
      typeName={typeName}
    />
  )
}
